<template>
  <v-container fluid>
    <h2 class="page-title mb-4">Ranking de Contratos</h2>

    <!-- Resumen: MRR promedio y moda por contrato -->
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="MRR Promedio por contrato"
          :firstValue="averageLabel"
          :secondValue="countLabel"
          :showCsvButton="false"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <CardResumeCompanyDetails
          titulo="MRR Moda por contrato"
          :firstValue="modeLabel"
          :secondValue="countLabel"
          :showCsvButton="false"
        />
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row class="mt-2">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="search"
          label="Buscar contrato o empresa"
          dense outlined hide-details clearable
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>
    </v-row>

    <v-chip-group v-model="filtroPais" class="mb-1">
      <v-chip value="" filter outlined small>Todos los países</v-chip>
      <v-chip v-for="p in paisesDisponibles" :key="p" :value="p" filter outlined small>{{ p }}</v-chip>
    </v-chip-group>

    <v-chip-group v-model="filtroLinea" class="mb-3">
      <v-chip value="" filter outlined small>Todas las líneas</v-chip>
      <v-chip v-for="l in lineasDisponibles" :key="l" :value="l" filter outlined small>{{ l }}</v-chip>
    </v-chip-group>

    <!-- Tabla -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :loading="loadingData"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [10, 20, 30, 50, 100] }"
          sort-by="mrrUSD"
          sort-desc
          class="elevation-1"
        >
          <template v-slot:item.canal="{ item }">
            <span class="grey--text text--darken-2">{{ item.canal || '—' }}</span>
          </template>
          <template v-slot:item.producto="{ item }">
            <span class="grey--text text--darken-2">{{ item.producto || '—' }}</span>
          </template>
          <template v-slot:item.mrrLocal="{ item }">
            {{ formatLocal(item.mrrLocal, item.moneda) }}
          </template>
          <template v-slot:item.mrrUSD="{ item }">
            {{ formatUSD(item.mrrUSD) }}
          </template>
          <template v-slot:no-data>
            <div class="py-4">Sin datos de MRR para el mes actual.</div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import CardResumeCompanyDetails from '../components/CardResumeCompanyDetails';
import MRRService from '../services/mrr.service';

export default {
  name: 'ContractRanking',
  mixins: [utilsMixin],
  components: { CardResumeCompanyDetails },
  data() {
    return {
      loadingData: false,
      rows: [],
      search: '',
      filtroPais: '',
      filtroLinea: '',
      headers: [
        { text: 'ID contrato',        value: 'contrato' },
        { text: 'Empresa',            value: 'empresa' },
        { text: 'País',               value: 'pais' },
        { text: 'Línea de negocio',   value: 'lineaNegocio' },
        { text: 'Broker',             value: 'canal' },
        { text: 'Producto',          value: 'producto' },
        { text: 'MRR Local',         value: 'mrrLocal', align: 'right' },
        { text: 'MRR USD',           value: 'mrrUSD', align: 'right' },
      ],
    };
  },
  computed: {
    paisesDisponibles() {
      return [...new Set(this.rows.map((r) => r.pais).filter(Boolean))].sort();
    },
    lineasDisponibles() {
      return [...new Set(this.rows.map((r) => r.lineaNegocio).filter(Boolean))].sort();
    },
    filteredRows() {
      const term = (this.search || '').trim().toLowerCase();
      return this.rows
        .filter((r) => !this.filtroPais || r.pais === this.filtroPais)
        .filter((r) => !this.filtroLinea || r.lineaNegocio === this.filtroLinea)
        .filter(
          (r) =>
            !term ||
            (r.contrato || '').toLowerCase().includes(term) ||
            (r.empresa || '').toLowerCase().includes(term)
        );
    },
    mrrUsdValues() {
      return this.filteredRows.map((r) => r.mrrUSD).filter((v) => v !== null && v !== undefined);
    },
    countLabel() {
      const n = this.filteredRows.length;
      return `de ${n} contrato${n === 1 ? '' : 's'}`;
    },
    averageLabel() {
      if (!this.mrrUsdValues.length) return '—';
      const total = this.mrrUsdValues.reduce((sum, v) => sum + v, 0);
      return this.formatUSD(total / this.mrrUsdValues.length);
    },
    modeLabel() {
      if (!this.mrrUsdValues.length) return '—';
      return this.formatUSD(this.mode(this.mrrUsdValues));
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loadingData = true;
      try {
        const res = await MRRService.getDetalleMRR();
        if (res.status === 'ok') {
          this.rows = this.groupByContrato(res.data);
        } else {
          this.showErrorSnackbar('No se pudo cargar el ranking de contratos');
        }
      } catch {
        this.showErrorSnackbar('No se pudo cargar el ranking de contratos');
      }
      this.loadingData = false;
    },

    // Un contrato puede tener más de una fila el mismo mes (ej. el
    // fallback de surplus EB); se agrupa por contrato sumando el MRR,
    // acotado al mes actual — igual criterio que MRR Resumen / MRR Hoy.
    groupByContrato(allRows) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const byContrato = new Map();
      for (const r of allRows) {
        if (!r.contrato || r.fecha?.slice(0, 7) !== currentMonth) continue;
        const existing = byContrato.get(r.contrato);
        if (existing) {
          existing.mrrUSD += r.mrrUSD || 0;
          if (existing.moneda === r.moneda) existing.mrrLocal += r.mrrLocal || 0;
        } else {
          byContrato.set(r.contrato, { ...r, mrrUSD: r.mrrUSD || 0, mrrLocal: r.mrrLocal || 0 });
        }
      }
      return [...byContrato.values()];
    },

    mode(values) {
      const frequency = new Map();
      for (const v of values) frequency.set(v, (frequency.get(v) ?? 0) + 1);
      let modeVal = values[0];
      let maxFrequency = 0;
      for (const [val, freq] of frequency.entries()) {
        if (freq > maxFrequency) {
          maxFrequency = freq;
          modeVal = val;
        } else if (freq === maxFrequency && val < modeVal) {
          modeVal = val;
        }
      }
      return modeVal;
    },

    formatUSD(val) {
      if (val == null) return '—';
      return `≈ US$${Math.round(val).toLocaleString('en-US')}`;
    },
    formatLocal(val, moneda) {
      if (val == null) return '—';
      const rounded = Math.round(val);
      if (moneda === 'USD') return `$${rounded.toLocaleString('en-US')}`;
      return `$${rounded.toLocaleString('es-CL')} ${moneda || ''}`;
    },
  },
};
</script>

<style scoped>
.page-title {
  color: #104b94;
  font-weight: 700;
}
</style>
